import { motion } from "framer-motion";
import { Github, Linkedin, Twitter, Mail, Terminal, ArrowUpRight, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const socials = [
  { icon: Github, label: "GitHub", href: "https://github.com/Anbu-2006" },
  { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/in/anbuselvan0306" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = (e) => {
    e.preventDefault();
    if (!email || !message) {
      toast.error("Please fill in both fields.");
      return;
    }
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setEmail("");
      setMessage("");
      toast.success("Message transmitted! I'll respond within 24 hours.");
    }, 1200);
  };

  return (
    <footer id="contact" className="py-24 px-6 relative overflow-hidden">
      {/* Subtle top separator */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute inset-0 data-grid opacity-20" />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left: Info */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            {/* Logo */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl glass-card-wasabi flex items-center justify-center">
                <Terminal className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="font-mono font-semibold text-sm text-foreground tracking-widest">
                  ANBU<span className="text-primary">.DEV</span>
                </div>
                <div className="font-mono text-xs text-muted-foreground tracking-wider">
                  AI & DATA ENGINEERING · VIBE CODER
                </div>
              </div>
            </div>

            <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mb-8">
              Building agentic AI systems, edge ML applications, and high-conversion
              web ecosystems. 2nd Year B.E. in AI & Data Engineering,
              based in Bengaluru, India.
            </p>

            {/* Social links */}
            <div className="flex gap-3 mb-8">
              {socials.map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 glass-card rounded-xl flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                  title={label}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>

            {/* Quick links */}
            <div className="space-y-2">
              {[
                { label: "VIEW GITHUB", href: "https://github.com/Anbu-2006" },
                { label: "AXIOM DIGITAL", href: "https://github.com/Axiom-Digital-Studios" },
                { label: "BEAST PROJECTS", href: "#projects" },
              ].map(({ label, href }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="flex items-center gap-2 font-mono text-xs text-muted-foreground hover:text-primary transition-colors group"
                >
                  <ArrowUpRight className="w-3 h-3 group-hover:text-primary" />
                  {label}
                </a>
              ))}
            </div>
          </motion.div>

          {/* Right: Contact form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="eyebrow mb-4">// OPEN_CHANNEL</div>
            <h3 className="font-display font-bold text-3xl text-foreground mb-2">
              Let&apos;s Build{" "}
              <span className="text-primary">Together</span>
            </h3>
            <p className="text-muted-foreground text-sm mb-7">
              Open to internships, collaborations, and freelance projects. Response within 24h.
            </p>

            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <label className="font-mono text-xs text-muted-foreground tracking-wider mb-1.5 block">
                  EMAIL_ADDRESS
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full glass-card rounded-xl px-4 py-3 text-sm text-foreground font-mono placeholder:text-muted-foreground/50 outline-none focus:border-primary/50 transition-colors"
                />
              </div>
              <div>
                <label className="font-mono text-xs text-muted-foreground tracking-wider mb-1.5 block">
                  MESSAGE
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell me about your project..."
                  rows={4}
                  className="w-full glass-card rounded-xl px-4 py-3 text-sm text-foreground font-mono placeholder:text-muted-foreground/50 outline-none focus:border-primary/50 transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="btn-wasabi w-full py-3.5 rounded-xl text-sm uppercase tracking-widest flex items-center justify-center gap-2"
              >
                {sending ? (
                  <>
                    <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    TRANSMITTING...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> SEND MESSAGE
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="font-mono text-xs text-muted-foreground tracking-wider">
            © 2025 ANBUSELVAN T. · ALL SYSTEMS OPERATIONAL
          </span>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-dot" />
            <span className="font-mono text-xs text-muted-foreground tracking-widest">
              BUILT WITH REACT + TAILWIND
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
